import type { NavigateFunction } from 'react-router-dom';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { openEtsyImportedOrderInProduction } from '@/services/etsyService';
import { activateWorkspaceDraft } from '@/services/workspaceDraftService';
import {
  buildProductionSupervisorSnapshot,
  extractDraftSupervisorSnapshot,
  hasMeaningfulSupervisorSnapshot,
  hydrateProductionSupervisorSnapshot,
  persistCurrentActiveWorkspaceDraft,
} from '@/services/workspaceDraftSnapshotService';

type OpenEtsyOrderInProductionFlowArgs = {
  readonly importedOrderId: string;
  readonly etsyReceiptId: string;
  readonly currentServiceId: string | null;
  readonly navigate: NavigateFunction;
};

export const openEtsyOrderInProductionFlow = async ({
  importedOrderId,
  etsyReceiptId,
  currentServiceId,
  navigate,
}: OpenEtsyOrderInProductionFlowArgs): Promise<void> => {
  try {
    analytics.track('button: ui.etsy_open_production_flow', {
      importedOrderId,
      etsyReceiptId,
    });

    await persistCurrentActiveWorkspaceDraft();

    const productionSession = await openEtsyImportedOrderInProduction(importedOrderId, {
      serviceId: currentServiceId,
    });

    const existingDraftSnapshot = extractDraftSupervisorSnapshot(productionSession.workspaceDraft?.snapshot);
    const hasReusableDraft = hasMeaningfulSupervisorSnapshot(existingDraftSnapshot);
    const hydratedReusableSnapshot = hasReusableDraft && existingDraftSnapshot
      ? hydrateProductionSupervisorSnapshot(existingDraftSnapshot, productionSession.order)
      : null;

    const draft = hasReusableDraft && productionSession.workspaceDraft
      ? await activateWorkspaceDraft({
          draftId: productionSession.workspaceDraft.id,
          snapshot: hydratedReusableSnapshot
            ? { supervisor: hydratedReusableSnapshot }
            : undefined,
        })
      : await activateWorkspaceDraft({
          ...(productionSession.workspaceDraft
            ? { draftId: productionSession.workspaceDraft.id }
            : {
                source: 'ETSY' as const,
                etsyImportedOrderId: importedOrderId,
              }),
          title: `Etsy receipt #${productionSession.importedOrder.etsyReceiptId}`,
          snapshot: {
            supervisor: buildProductionSupervisorSnapshot(productionSession.order),
          },
          metadata: {
            kind: 'etsy-order',
            etsyImportedOrderId: productionSession.importedOrder.id,
            etsyReceiptId: productionSession.importedOrder.etsyReceiptId,
            shopName: productionSession.importedOrder.shopName,
            buyerName: productionSession.importedOrder.buyerName,
            orderId: productionSession.order.id,
          },
        });

    navigate(`/studio?draftId=${draft.id}&stage=uploadedImages`);
  } catch (error) {
    logging.error('Failed to open Etsy order in production flow', {
      error,
      importedOrderId,
      etsyReceiptId,
    });
    throw error;
  }
};
